import { setCurrentEditorId } from '../auth/jwtStorage';
import { brainBaseUrl } from '../rest/restClient';
import { WebSocketClosedError, WebSocketRequestError, } from './envelope';
/**
 * Opens a WebSocket to the brain, waits for the `welcome` frame, and
 * exposes a typed request/response + push-subscription API on top.
 *
 * Browser WebSocket cannot send custom HTTP headers, so JWT and
 * client-identity travel as query parameters
 * (`?token=&profile=&clientVersion=`). The server's
 * {@code BrainAccessFilter} and {@code VanceHandshakeInterceptor}
 * accept those for the WS upgrade route — see
 * `specification/websocket-protokoll.md` §2.
 *
 * Lifecycle expectations:
 *  - {@link connect} returns a fresh, ready instance once `welcome` arrives.
 *  - The caller subscribes to server-pushed frames via {@link on}.
 *  - {@link send} resolves with the matching `replyTo` frame, or rejects
 *    with a {@link WebSocketRequestError} (server-sent `error`) or
 *    {@link WebSocketClosedError} (connection died first).
 *  - Reconnects are explicit: when the socket closes, the caller decides
 *    whether to open a fresh instance.
 */
export class BrainWebSocket {
    socket;
    handlers = new Map();
    /**
     * Handlers for non-session channels (documents, notify, …). Keyed by
     * {@code `${channel}:${type}`} so the same {@code type} string on
     * different channels routes to its own subscriber set.
     */
    channelHandlers = new Map();
    pending = new Map();
    closeListeners = new Set();
    requestSeq = 0;
    tenantId;
    welcome = null;
    isClosed = false;
    keepAliveTimer = null;
    /**
     * Bound session id, mirrored from the server side. Filled when a
     * `session-create` or `session-resume` reply arrives, cleared on
     * `session-unbind`. Travels in the outer {@link LiveEnvelope} so the
     * Face-Pod can route session-channel frames to the project's home-pod
     * (see planning/live-ws.md §5.2, §7).
     */
    currentSessionId = null;
    constructor(socket, tenant) {
        this.socket = socket;
        this.tenantId = tenant;
        this.socket.addEventListener('message', this.handleMessage);
        this.socket.addEventListener('close', this.handleClose);
    }
    /**
     * Open a connection and resolve once the server's `welcome` frame
     * has arrived. Rejects if the upgrade fails (HTTP error from the
     * filter) or the socket closes before `welcome`.
     */
    static connect(options) {
        const url = options.url ?? buildBrainWsUrl(options);
        const socket = new WebSocket(url);
        const instance = new BrainWebSocket(socket, options.tenant);
        return new Promise((resolve, reject) => {
            let settled = false;
            const onWelcome = (data) => {
                if (settled)
                    return;
                settled = true;
                instance.welcome = data;
                // Expose the per-connection editorId in the module-level
                // identity slot so REST writes (brainSendRaw, brainFetch) can
                // forward it as the X-Editor-Id header — without this the brain
                // can't distinguish "I (the writer) just saved" from "someone
                // else saved", and the writer's tab sees its own banner.
                setCurrentEditorId(data.editorId);
                // Brain expects client-driven heartbeat pings (see vance-foot's
                // ConnectionService.startKeepAlive). Without these the server's
                // session bookkeeping treats the connection as idle and may
                // close it — same contract as Foot, same interval.
                instance.startKeepAlive(data.server.pingInterval);
                resolve(instance);
            };
            instance.on('welcome', onWelcome);
            // If the socket dies before we get a welcome, fail the connect.
            const onCloseBeforeReady = (event) => {
                if (settled)
                    return;
                settled = true;
                socket.removeEventListener('close', onCloseBeforeReady);
                reject(new WebSocketClosedError(`WebSocket closed before welcome (code ${event.code})`));
            };
            socket.addEventListener('close', onCloseBeforeReady);
            // Likewise an explicit error event from the underlying socket.
            socket.addEventListener('error', () => {
                if (settled)
                    return;
                settled = true;
                reject(new WebSocketClosedError('WebSocket error during handshake'));
            }, { once: true });
        });
    }
    /**
     * Send a request frame and wait for its reply (matched on
     * {@code replyTo === id}). Rejects on server `error` reply or socket
     * close.
     */
    send(type, data) {
        if (this.isClosed) {
            return Promise.reject(new WebSocketClosedError());
        }
        const id = `req_${++this.requestSeq}`;
        const envelope = { id, type, data };
        return new Promise((resolve, reject) => {
            this.pending.set(id, {
                type,
                resolve: (raw) => resolve(raw),
                reject,
            });
            this.socket.send(JSON.stringify(this.wrapForLive(envelope)));
        });
    }
    /** Send a frame without expecting a reply (e.g. `logout`). */
    sendNoReply(type, data) {
        if (this.isClosed)
            return;
        const envelope = { type, data };
        this.socket.send(JSON.stringify(this.wrapForLive(envelope)));
        // `session-unbind` is fire-and-forget — clear the cached id now so the
        // next request's LiveEnvelope reflects the unbound state.
        if (type === 'session-unbind') {
            this.currentSessionId = null;
        }
    }
    /**
     * Send a fire-and-forget frame on a non-default channel (e.g.
     * {@code documents}). The outer envelope's {@code sessionId} is left
     * empty — documents/notify/progress frames are not session-scoped.
     */
    sendOnChannel(channel, type, data) {
        if (this.isClosed)
            return;
        const envelope = { type, data };
        const live = { channel, payload: envelope };
        this.socket.send(JSON.stringify(live));
    }
    /**
     * Subscribe to server-pushed frames of {@code type} on a non-default
     * channel. Returns an unsubscribe function. Mirror of {@link #on} but
     * for {@code documents}/{@code notify}/{@code progress} channels.
     */
    onChannel(channel, type, handler) {
        const key = `${channel}:${type}`;
        let set = this.channelHandlers.get(key);
        if (!set) {
            set = new Set();
            this.channelHandlers.set(key, set);
        }
        set.add(handler);
        return () => set.delete(handler);
    }
    wrapForLive(envelope) {
        return {
            channel: 'session',
            sessionId: this.currentSessionId ?? undefined,
            payload: envelope,
        };
    }
    /**
     * Subscribe to server-pushed frames of {@code type}. Returns an
     * unsubscribe function. Multiple handlers per type are allowed.
     *
     * Pushed frames have no {@code replyTo} — the dispatcher routes
     * frames with {@code replyTo} to the pending-request map instead.
     */
    on(type, handler) {
        let set = this.handlers.get(type);
        if (!set) {
            set = new Set();
            this.handlers.set(type, set);
        }
        set.add(handler);
        return () => set.delete(handler);
    }
    /** Subscribe to the underlying socket close event. */
    onClose(handler) {
        this.closeListeners.add(handler);
        return () => this.closeListeners.delete(handler);
    }
    /** Welcome payload as received during connect. {@code null} only before {@link connect} resolves. */
    getWelcome() {
        return this.welcome;
    }
    /** Tenant id this connection is scoped to. */
    getTenantId() {
        return this.tenantId;
    }
    /** Whether the socket has been observed closed (or close() called). */
    closed() {
        return this.isClosed;
    }
    /** Close the connection. Pending requests are rejected with {@link WebSocketClosedError}. */
    close(code, reason) {
        if (this.isClosed)
            return;
        this.socket.close(code, reason);
    }
    // ── internals ───────────────────────────────────────────────────────
    handleMessage = (event) => {
        const raw = typeof event.data === 'string' ? event.data : '';
        if (!raw)
            return;
        let outer;
        try {
            outer = JSON.parse(raw);
        }
        catch {
            // Malformed frame — log to console; protocol violation, but don't
            // crash the editor.
            console.warn('Discarding non-JSON WebSocket frame', raw);
            return;
        }
        if (outer.payload === undefined || outer.payload === null) {
            console.warn('Discarding live frame with empty payload', outer.channel);
            return;
        }
        const envelope = outer.payload;
        // Non-session channels (documents, notify, …) get their own
        // handler-set lookup; they never participate in pending-request
        // matching since they don't carry an `id`/`replyTo`.
        if (outer.channel && outer.channel !== 'session') {
            const channelKey = `${outer.channel}:${envelope.type}`;
            const channelSubscribers = this.channelHandlers.get(channelKey);
            if (channelSubscribers && channelSubscribers.size > 0) {
                for (const h of channelSubscribers) {
                    try {
                        h(envelope.data);
                    }
                    catch (e) {
                        console.error(`WS handler for '${channelKey}' threw`, e);
                    }
                }
            }
            return;
        }
        // Reply path: a request the client is waiting for.
        if (envelope.replyTo) {
            const pending = this.pending.get(envelope.replyTo);
            if (pending) {
                this.pending.delete(envelope.replyTo);
                if (envelope.type === 'error') {
                    const err = envelope.data;
                    pending.reject(new WebSocketRequestError(err?.errorCode ?? 0, pending.type, err?.errorMessage ?? `Server error on ${pending.type}`, err?.reason));
                }
                else {
                    // Auto-track the bound sessionId so subsequent frames carry it
                    // in the LiveEnvelope (face-pod routing needs it for everything
                    // except session-create/resume/bootstrap themselves).
                    if (pending.type === 'session-create'
                        || pending.type === 'session-resume'
                        || pending.type === 'session-bootstrap') {
                        const sid = envelope.data?.sessionId;
                        if (typeof sid === 'string' && sid.length > 0) {
                            this.currentSessionId = sid;
                        }
                    }
                    pending.resolve(envelope.data);
                }
                return;
            }
            // Else fall through — replyTo with no pending request gets
            // dispatched to handlers in case someone wants to observe it.
        }
        // Push path: dispatch to subscribers.
        const subscribers = this.handlers.get(envelope.type);
        if (subscribers && subscribers.size > 0) {
            for (const handler of subscribers) {
                try {
                    handler(envelope.data);
                }
                catch (e) {
                    console.error(`WS handler for '${envelope.type}' threw`, e);
                }
            }
        }
    };
    startKeepAlive(intervalSeconds) {
        this.stopKeepAlive();
        if (intervalSeconds <= 0)
            return;
        this.keepAliveTimer = setInterval(() => { void this.sendKeepAlivePing(); }, intervalSeconds * 1000);
    }
    stopKeepAlive() {
        if (this.keepAliveTimer !== null) {
            clearInterval(this.keepAliveTimer);
            this.keepAliveTimer = null;
        }
    }
    async sendKeepAlivePing() {
        if (this.isClosed)
            return;
        try {
            await this.send('ping', {
                clientTimestamp: Date.now(),
            });
        }
        catch (e) {
            // Connection died or server rejected the ping. The close handler
            // will stop the loop; just log so the failure is visible during
            // dev. No surface to users — reconnect-on-send recovers.
            console.warn('WebSocket keep-alive ping failed', e);
        }
    }
    handleClose = (event) => {
        this.isClosed = true;
        this.stopKeepAlive();
        // The editorId is socket-scoped; clear it so a stale value isn't
        // accidentally attached to REST writes after disconnect. The next
        // welcome on a fresh socket sets a new one.
        setCurrentEditorId(null);
        for (const [id, pending] of this.pending) {
            pending.reject(new WebSocketClosedError(`WebSocket closed (code ${event.code}) — request '${pending.type}' (${id}) abandoned`));
        }
        this.pending.clear();
        for (const listener of this.closeListeners) {
            try {
                listener(event);
            }
            catch (e) {
                console.error('WS close listener threw', e);
            }
        }
    };
}
/**
 * Construct the WebSocket URL with auth + client-identity passed as
 * query parameters. The base URL comes from
 * {@link configurePlatform} — the host (Web's `bootWeb.ts`) is
 * responsible for substituting `${location.protocol}//${location.host}`
 * before binding when same-origin connections are wanted; this module
 * never reads the platform URL itself.
 */
function buildBrainWsUrl(options) {
    const httpBase = brainBaseUrl();
    if (!httpBase) {
        throw new Error('@vance/shared: brain base URL is empty — call configurePlatform with a non-empty baseUrl before opening WebSocket.');
    }
    const wsOrigin = httpBase
        .replace(/^http:\/\//, 'ws://')
        .replace(/^https:\/\//, 'wss://');
    const params = new URLSearchParams({
        profile: options.profile,
        clientVersion: options.clientVersion,
    });
    // Cookie-only callers (web UI same-origin) leave `jwt` empty; the
    // browser ships `vance_access` on the upgrade request. Bearer
    // callers (Mobile, cross-origin embeds) pass the access token here
    // — it is rendered as the `?token=` query parameter because the
    // browser WebSocket constructor cannot set custom headers.
    if (options.jwt) {
        params.set('token', options.jwt);
    }
    // Platform context for exec-capable clients — the Facelift desktop
    // app cannot set handshake headers from its web view, so the same
    // JSON payload travels as a query parameter (parsed identically
    // server-side, handshake-final, never fatal).
    if (options.clientContext) {
        params.set('clientContext', JSON.stringify(options.clientContext));
    }
    return `${wsOrigin}/brain/${encodeURIComponent(options.tenant)}/ws?${params}`;
}
//# sourceMappingURL=brainWebSocket.js.map